import React, { useEffect } from 'react';
import { Card } from '../components/ui/card';
import { MapPin, Waves, Trees, ShoppingBag, Store, Train, Fish, Mountain } from 'lucide-react';
import Header from '../components/Header';
import { useLanguage } from '../context/LanguageContext';

const DieUmgebung = () => {
  const { t } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="pt-28 pb-12 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">{t('area.title')}</h1>
          <h2 className="text-xl md:text-2xl text-amber-600 font-semibold mb-6">{t('area.subtitle')}</h2>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
            {t('area.intro')}
          </p>
        </div>
      </section>

      {/* Strände und Küste */}
      <section className="py-12 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center gap-3 mb-8">
            <Waves className="w-8 h-8 text-amber-600" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800">{t('area.beachesTitle')}</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="overflow-hidden border-amber-200 hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/3099848/pexels-photo-3099848.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt={t('area.pranburiBeach')}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('area.pranburiBeach')}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {t('area.pranburiBeachDesc')}
                </p>
              </div>
            </Card>

            <Card className="overflow-hidden border-amber-200 hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/35493559/pexels-photo-35493559.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt={t('area.laemPromThep')}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('area.laemPromThep')}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {t('area.laemPromThepDesc')}
                </p>
              </div>
            </Card>

            <Card className="overflow-hidden border-amber-200 hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/5735906/pexels-photo-5735906.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt={t('area.khaoKalok')}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('area.khaoKalok')}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {t('area.khaoKalokDesc')}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Natur & Outdoor */}
      <section className="py-12 px-4 bg-gradient-to-b from-green-50 to-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center gap-3 mb-8">
            <Trees className="w-8 h-8 text-green-600" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800">{t('area.natureTitle')}</h2>
          </div>
          
          <div className="space-y-6">
            <Card className="overflow-hidden border-green-200 hover:shadow-xl transition-shadow duration-300">
              <div className="md:flex">
                <div className="md:w-1/3 h-64 md:h-auto overflow-hidden">
                  <img 
                    src="https://images.pexels.com/photos/8300243/pexels-photo-8300243.jpeg?auto=compress&cs=tinysrgb&w=800" 
                    alt={t('area.forestPark')}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:w-2/3 p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('area.forestPark')}</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {t('area.forestParkDesc')}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="overflow-hidden border-green-200 hover:shadow-xl transition-shadow duration-300">
              <div className="md:flex">
                <div className="md:w-1/3 h-64 md:h-auto overflow-hidden">
                  <img 
                    src="https://images.pexels.com/photos/14277720/pexels-photo-14277720.jpeg?auto=compress&cs=tinysrgb&w=800" 
                    alt={t('area.nationalPark')}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:w-2/3 p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('area.nationalPark')}</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {t('area.nationalParkDesc')}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="overflow-hidden border-green-200 hover:shadow-xl transition-shadow duration-300">
              <div className="md:flex">
                <div className="md:w-1/3 h-64 md:h-auto overflow-hidden">
                  <img 
                    src="https://images.pexels.com/photos/1006339/pexels-photo-1006339.jpeg?auto=compress&cs=tinysrgb&w=800" 
                    alt={t('area.riverBoat')}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:w-2/3 p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('area.riverBoat')}</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {t('area.riverBoatDesc')}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Lokale Märkte */}
      <section className="py-12 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center gap-3 mb-8">
            <ShoppingBag className="w-8 h-8 text-amber-600" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800">{t('area.marketsTitle')}</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="overflow-hidden border-amber-200 hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/35469450/pexels-photo-35469450.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt={t('area.oldMarket')}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('area.oldMarket')}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {t('area.oldMarketDesc')}
                </p>
              </div>
            </Card>

            <Card className="overflow-hidden border-amber-200 hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/11967509/pexels-photo-11967509.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt={t('area.fishermanVillage')}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('area.fishermanVillage')}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {t('area.fishermanVillageDesc')}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* 7-Eleven Section */}
      <section className="py-12 px-4 bg-gradient-to-b from-green-50 to-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center gap-3 mb-8">
            <Store className="w-8 h-8 text-green-600" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800">{t('area.sevenElevenTitle')}</h2>
          </div>
          
          <Card className="p-6 md:p-8 border-green-200 overflow-hidden">
            <div className="md:flex gap-8 items-start">
              <div className="md:w-1/4 mb-6 md:mb-0 flex justify-center">
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/4/40/7-eleven_logo.svg/400px-7-eleven_logo.svg.png" 
                  alt="7-Eleven Logo"
                  className="w-40 h-auto"
                />
              </div>
              <div className="md:w-3/4">
                <p className="text-gray-600 leading-relaxed mb-6">
                  {t('area.sevenElevenDesc')}
                </p>
                
                <h4 className="font-semibold text-gray-900 mb-4">{t('area.sevenElevenUseful')}</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <p className="text-gray-600">{t('area.sevenElevenItem1')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <p className="text-gray-600">{t('area.sevenElevenItem2')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <p className="text-gray-600">{t('area.sevenElevenItem3')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <p className="text-gray-600">{t('area.sevenElevenItem4')}</p>
                  </div>
                </div>
                
                <p className="text-gray-600 leading-relaxed mt-6">
                  {t('area.sevenElevenFooter')}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Hua Hin Section */}
      <section className="py-12 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="w-8 h-8 text-amber-600" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800">{t('area.huaHinTitle')}</h2>
          </div>
          <p className="text-lg text-gray-600 mb-8">
            {t('area.huaHinIntro')}
          </p>

          {/* Historische Highlights */}
          <div className="mb-10">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Train className="w-5 h-5 text-amber-600" />
              {t('area.historicTitle')}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="overflow-hidden border-amber-200 hover:shadow-xl transition-shadow duration-300">
                <div className="h-48 overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1572276596237-5db2c3e16c5d?w=800&auto=format&fit=crop&q=60" 
                    alt={t('area.railwayStation')}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('area.railwayStation')}</h4>
                  <p className="text-gray-600">
                    {t('area.railwayStationDesc')}
                  </p>
                </div>
              </Card>
              <Card className="overflow-hidden border-amber-200 hover:shadow-xl transition-shadow duration-300">
                <div className="h-48 overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=800&auto=format&fit=crop&q=60" 
                    alt={t('area.rajabhaktiPark')}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('area.rajabhaktiPark')}</h4>
                  <p className="text-gray-600">
                    {t('area.rajabhaktiParkDesc')}
                  </p>
                </div>
              </Card>
            </div>
          </div>

          {/* Strände */}
          <div className="mb-10">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Waves className="w-5 h-5 text-blue-600" />
              {t('area.beachesOutdoor')}
            </h3>
            <Card className="overflow-hidden border-blue-200 hover:shadow-xl transition-shadow duration-300">
              <div className="md:flex">
                <div className="md:w-1/3 h-48 md:h-auto overflow-hidden">
                  <img 
                    src="https://images.pexels.com/photos/3099848/pexels-photo-3099848.jpeg?auto=compress&cs=tinysrgb&w=800" 
                    alt={t('area.suanSonBeach')}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:w-2/3 p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('area.suanSonBeach')}</h4>
                  <p className="text-gray-600">
                    {t('area.suanSonBeachDesc')}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Märkte & Nachtleben */}
          <div>
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-purple-600" />
              {t('area.marketsNightlife')}
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="overflow-hidden border-purple-200 hover:shadow-xl transition-shadow duration-300">
                <div className="h-40 overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1504940892017-d23b9f731c5e?w=800&auto=format&fit=crop&q=60" 
                    alt={t('area.nightMarket')}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('area.nightMarket')}</h4>
                  <p className="text-gray-600 text-sm">
                    {t('area.nightMarketDesc')}
                  </p>
                </div>
              </Card>
              <Card className="overflow-hidden border-purple-200 hover:shadow-xl transition-shadow duration-300">
                <div className="h-40 overflow-hidden">
                  <img 
                    src="https://images.pexels.com/photos/35469450/pexels-photo-35469450.jpeg?auto=compress&cs=tinysrgb&w=800" 
                    alt={t('area.cicadaMarket')}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('area.cicadaMarket')}</h4>
                  <p className="text-gray-600 text-sm">
                    {t('area.cicadaMarketDesc')}
                  </p>
                </div>
              </Card>
              <Card className="overflow-hidden border-purple-200 hover:shadow-xl transition-shadow duration-300">
                <div className="h-40 overflow-hidden">
                  <img 
                    src="https://images.pexels.com/photos/14277722/pexels-photo-14277722.jpeg?auto=compress&cs=tinysrgb&w=800" 
                    alt={t('area.plearnWan')}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('area.plearnWan')}</h4>
                  <p className="text-gray-600 text-sm">
                    {t('area.plearnWanDesc')}
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 mt-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400">
            {t('footer.copyright')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DieUmgebung;
